import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    key: String,
    steps: Array,
    dismissUrl: String
  }

  connect() {
    this.currentIndex = 0
    this.spotlight = null
    this.popover = null
    this.ring = null
    this.scrim = null
    this.boundReposition = this.reposition.bind(this)

    requestAnimationFrame(() => this.showStep())
  }

  disconnect() {
    this.cleanup()
    window.removeEventListener("resize", this.boundReposition)
  }

  showStep() {
    const step = this.stepsValue[this.currentIndex]
    if (!step) { this.dismiss(); return }

    const target = document.querySelector(step.selector)
    if (!target) {
      if (this.currentIndex < this.stepsValue.length - 1) {
        this.currentIndex++
        this.showStep()
      } else {
        this.dismiss()
      }
      return
    }

    const rect = target.getBoundingClientRect()
    const pad = 8
    const radius = this.getRadius(target, pad)

    this.ensureScrim()
    this.ensureSpotlight(rect, pad, radius)
    this.ensureRing(rect, pad, radius)
    this.ensurePopover(step, rect, pad)

    this.currentTarget = target
    this.targetClickHandler = () => this.dismiss()
    target.addEventListener("click", this.targetClickHandler, { once: true })

    window.addEventListener("resize", this.boundReposition)
  }

  // ── Scrim (click-catcher behind spotlight) ──

  ensureScrim() {
    if (this.scrim) return
    this.scrim = document.createElement("div")
    this.scrim.className = "feature-tour-scrim"
    this.scrim.addEventListener("click", () => this.dismiss())
    document.body.appendChild(this.scrim)
  }

  // ── Spotlight (box-shadow approach for rounded cutout) ──

  ensureSpotlight(rect, pad, radius) {
    if (!this.spotlight) {
      this.spotlight = document.createElement("div")
      this.spotlight.className = "feature-tour-spotlight"
      document.body.appendChild(this.spotlight)
    }
    this.spotlight.style.top = `${rect.top - pad}px`
    this.spotlight.style.left = `${rect.left - pad}px`
    this.spotlight.style.width = `${rect.width + pad * 2}px`
    this.spotlight.style.height = `${rect.height + pad * 2}px`
    this.spotlight.style.borderRadius = `${radius}px`
  }

  // ── Ring ──

  ensureRing(rect, pad, radius) {
    if (!this.ring) {
      this.ring = document.createElement("div")
      this.ring.className = "feature-tour-ring"
      document.body.appendChild(this.ring)
    }
    this.ring.style.top = `${rect.top - pad}px`
    this.ring.style.left = `${rect.left - pad}px`
    this.ring.style.width = `${rect.width + pad * 2}px`
    this.ring.style.height = `${rect.height + pad * 2}px`
    this.ring.style.borderRadius = `${radius}px`
  }

  // ── Popover ──

  ensurePopover(step, rect, pad) {
    if (this.popover) this.popover.remove()

    this.popover = document.createElement("div")
    this.popover.className = "feature-tour-popover"

    const isLastStep = this.currentIndex === this.stepsValue.length - 1
    const stepCount = this.stepsValue.length
    const btnLabel = isLastStep ? "Got it" : "Next"

    this.popover.innerHTML = `
      <div class="feature-tour-popover__arrow"></div>
      <div class="feature-tour-popover__title">${this.escapeHtml(step.title)}</div>
      <div class="feature-tour-popover__description">${this.escapeHtml(step.description)}</div>
      <div class="feature-tour-popover__footer">
        ${stepCount > 1 ? `<span class="feature-tour-popover__step">${this.currentIndex + 1} / ${stepCount}</span>` : '<span></span>'}
        <button class="feature-tour-popover__btn" type="button">${btnLabel}</button>
      </div>
    `

    const btn = this.popover.querySelector(".feature-tour-popover__btn")
    btn.addEventListener("click", () => {
      if (isLastStep) {
        this.dismiss()
      } else {
        this.currentIndex++
        this.showStep()
      }
    })

    document.body.appendChild(this.popover)
    this.positionPopover(rect, pad)
  }

  positionPopover(rect, pad) {
    const popoverRect = this.popover.getBoundingClientRect()
    const gap = 12
    const cutoutBottom = rect.bottom + pad + gap
    const cutoutTop = rect.top - pad - gap
    const arrow = this.popover.querySelector(".feature-tour-popover__arrow")

    const below = cutoutBottom + popoverRect.height < window.innerHeight
    if (below) {
      this.popover.style.top = `${cutoutBottom}px`
      arrow.className = "feature-tour-popover__arrow feature-tour-popover__arrow--top"
    } else {
      this.popover.style.top = `${cutoutTop - popoverRect.height}px`
      arrow.className = "feature-tour-popover__arrow feature-tour-popover__arrow--bottom"
    }

    // Center horizontally on target, clamp to viewport
    let left = rect.left + rect.width / 2 - popoverRect.width / 2
    left = Math.max(12, Math.min(left, window.innerWidth - popoverRect.width - 12))
    this.popover.style.left = `${left}px`

    // Position arrow horizontally to point at target center
    const targetCenterX = rect.left + rect.width / 2
    const arrowLeft = targetCenterX - left
    arrow.style.left = `${Math.max(20, Math.min(arrowLeft, popoverRect.width - 20))}px`
  }

  // ── Helpers ──

  getRadius(target, pad) {
    const computed = getComputedStyle(target)
    const raw = parseFloat(computed.borderRadius) || 0
    // If the target is fully round (circle), keep it round
    if (raw >= target.offsetWidth / 2) return (target.offsetWidth / 2) + pad
    return raw > 0 ? raw + pad / 2 : 8
  }

  // ── Lifecycle ──

  reposition() {
    const step = this.stepsValue[this.currentIndex]
    if (!step) return
    const target = document.querySelector(step.selector)
    if (!target) return
    const rect = target.getBoundingClientRect()
    const pad = 8
    const radius = this.getRadius(target, pad)
    this.ensureSpotlight(rect, pad, radius)
    this.ensureRing(rect, pad, radius)
    this.positionPopover(rect, pad)
  }

  dismiss() {
    this.cleanup()
    this.persistDismissal()
  }

  cleanup() {
    if (this.scrim) { this.scrim.remove(); this.scrim = null }
    if (this.spotlight) { this.spotlight.remove(); this.spotlight = null }
    if (this.popover) { this.popover.remove(); this.popover = null }
    if (this.ring) { this.ring.remove(); this.ring = null }
    if (this.currentTarget && this.targetClickHandler) {
      this.currentTarget.removeEventListener("click", this.targetClickHandler)
    }
    window.removeEventListener("resize", this.boundReposition)
  }

  persistDismissal() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.dismissUrlValue, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": token,
        "Accept": "application/json"
      },
      credentials: "same-origin"
    })
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
